import { ReactElement, useMemo, useCallback } from 'react';
import {
  DialogTitle,
  DialogContent,
  DialogContentText,
  Stack,
  Avatar,
  TextField,
  InputAdornment,
  DialogActions,
  Button,
  createFilterOptions,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTruckFast as VehicleIcon } from '@fortawesome/free-solid-svg-icons';
import { useSnackbar } from 'notistack';
import { useFormik, FormikHelpers } from 'formik';
import { find } from 'lodash';

import { useLazyRequest } from '~hooks/useLazyRequest';
import { updateRouteConfig } from '~services/route';
import CustomAutocomplete from '~components/CustomAutocomplete';
import VehicleInfo from '~components/VehicleInfo';
import UserInfo from '~components/UserInfo';
import DurationField from '~components/DurationField';
import { hasError, getHelperText } from '~utils/formHelpers';
import { getDefaultAvatar } from '~utils/user';

import { DialogUpdateCompletedRouteContentProps } from './types';
import {
  FIELDS_NAME,
  getInitialValues,
  validationSchema,
  getParseKmToMeters,
} from './utils';

const DialogUpdateCompletedRouteContent = ({
  onClose,
  routeInfo,
  vehicles,
  drivers,
  onSuccessSubmit,
}: DialogUpdateCompletedRouteContentProps): ReactElement => {
  const { enqueueSnackbar } = useSnackbar();

  const title = useMemo(() => {
    const routeCode = routeInfo.routeCode.toUpperCase();

    return `Editar informacion de ruta ${routeCode}`;
  }, [routeInfo.routeCode]);

  const [, loadingUpdateRoute, , executeGenerateUpdateRoute] = useLazyRequest({
    request: updateRouteConfig,
    withPostSuccess: (response) => {
      const currentRouteCode = response.data?.data.routeCode.toUpperCase();

      enqueueSnackbar(
        `La informacion de la ruta ${currentRouteCode} fue editada correctamente`,
        { variant: 'success' },
      );

      onSuccessSubmit?.();

      onClose();
    },
    withPostFailure: (err) => {
      let errorMessage = 'Ha ocurrido un error, intente nuevamente';

      if (err?.data?.data.message) {
        errorMessage = err.data.data.message;
      }

      enqueueSnackbar(errorMessage, { variant: 'error' });
    },
  });

  const onSubmit = useCallback(
    async (
      formValues: ReturnType<typeof getInitialValues>,
      { setSubmitting }: FormikHelpers<ReturnType<typeof getInitialValues>>,
    ) => {
      const totalKm = getParseKmToMeters(
        Number(formValues[FIELDS_NAME.TOTAL_KM]),
      );

      await executeGenerateUpdateRoute({
        routeId: routeInfo.routeId,
        driverId: formValues[FIELDS_NAME.DRIVER],
        vehicleId: formValues[FIELDS_NAME.VEHICLE],
        totalKm,
        totalTime: formValues[FIELDS_NAME.TOTAL_TIME],
        routeCompletedMode: true,
      });

      setSubmitting(false);
    },
    [executeGenerateUpdateRoute, routeInfo.routeId],
  );

  const {
    errors,
    touched,
    getFieldProps,
    setFieldValue,
    setFieldTouched,
    submitForm,
    dirty,
    isValid,
    isSubmitting,
  } = useFormik({
    initialValues: getInitialValues(routeInfo),
    validationSchema,
    onSubmit,
  });

  const defaultDriver = useMemo(
    () =>
      routeInfo.driverId
        ? find(drivers, { id: routeInfo.driverId })
        : undefined,
    [routeInfo.driverId, drivers],
  );

  const defaultVehicle = useMemo(
    () => find(vehicles, { id: routeInfo.vehicleId }),
    [routeInfo.vehicleId, vehicles],
  );

  return (
    <>
      <DialogTitle>{title}</DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          <DialogContentText color="text.secondary" variant="caption">
            Establezca los nuevos valores que desea editar.
          </DialogContentText>

          <CustomAutocomplete
            options={drivers}
            fullWidth
            disableClearable
            label="Chofer asignado"
            name={FIELDS_NAME.DRIVER}
            defaultValue={defaultDriver}
            onChange={(_e, newDriver) => {
              setFieldValue(FIELDS_NAME.DRIVER, newDriver.id);
            }}
            onBlur={() => setFieldTouched(FIELDS_NAME.DRIVER, true)}
            error={hasError(touched, errors, FIELDS_NAME.DRIVER)}
            helperText={getHelperText(touched, errors, FIELDS_NAME.DRIVER)}
            getOptionLabel={(option) => option.displayName}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                <UserInfo
                  name={option.displayName}
                  urlAvatar={getDefaultAvatar(option.avatar ?? undefined)}
                />
              </li>
            )}
            startAdornment={(val) =>
              val && (
                <Avatar
                  src={getDefaultAvatar(val.avatar ?? undefined)}
                  alt={val.displayName}
                  sx={{ width: 20, height: 20 }}
                />
              )
            }
            filterOptions={createFilterOptions({
              ignoreAccents: true,
              ignoreCase: true,
              stringify: (option) => option.displayName.replace(',', ''),
            })}
          />

          <CustomAutocomplete
            options={vehicles}
            fullWidth
            disableClearable
            label="Vehículo asociado"
            name={FIELDS_NAME.VEHICLE}
            defaultValue={defaultVehicle}
            onChange={(_e, newVehicle) => {
              setFieldValue(FIELDS_NAME.VEHICLE, newVehicle.id);
            }}
            onBlur={() => setFieldTouched(FIELDS_NAME.VEHICLE, true)}
            error={hasError(touched, errors, FIELDS_NAME.VEHICLE)}
            helperText={getHelperText(touched, errors, FIELDS_NAME.VEHICLE)}
            getOptionLabel={(option) =>
              `${option?.displayName}${option?.code ? ` (${option.code})` : ''}`
            }
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                <VehicleInfo
                  vehicle={option.displayName}
                  vehicleCode={option.code as string}
                  color={option.color}
                />
              </li>
            )}
            startAdornment={(val) =>
              val && (
                <FontAwesomeIcon
                  icon={VehicleIcon}
                  style={{ color: val.color, width: 20, height: 20 }}
                />
              )
            }
          />

          <TextField
            {...getFieldProps(FIELDS_NAME.TOTAL_KM)}
            label="KM recorridos reales"
            type="number"
            error={hasError(touched, errors, FIELDS_NAME.TOTAL_KM)}
            helperText={getHelperText(touched, errors, FIELDS_NAME.TOTAL_KM)}
            fullWidth
            autoComplete="off"
            InputProps={{
              endAdornment: <InputAdornment position="end">KM</InputAdornment>,
            }}
          />

          <DurationField
            label="Tiempo de viaje"
            defaultValue={routeInfo.durationReal ?? 0}
            onChange={(totalSeconds) =>
              setFieldValue(FIELDS_NAME.TOTAL_TIME, totalSeconds)
            }
            onBlur={() => setFieldTouched(FIELDS_NAME.TOTAL_TIME, true)}
            error={hasError(touched, errors, FIELDS_NAME.TOTAL_TIME)}
            helperText={getHelperText(touched, errors, FIELDS_NAME.TOTAL_TIME)}
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Button color="secondary" onClick={onClose}>
          Cerrar
        </Button>

        <LoadingButton
          variant="contained"
          color="primary"
          onClick={submitForm}
          loading={loadingUpdateRoute || isSubmitting}
          disabled={!dirty || !isValid}
        >
          Guardar cambios
        </LoadingButton>
      </DialogActions>
    </>
  );
};

export default DialogUpdateCompletedRouteContent;
